<mat-card appearance="outlined" [attr.data-test-id]="'module-card-' + config.guid">
  <mat-card-header>
    <mat-card-title>
      {{ config.headline }}
    </mat-card-title>
    <mat-card-subtitle>
      @if (config.filter.enabled) {
        <div class="data-filtered-message">
          <mat-icon class="mr-1">info</mat-icon> {{ "app.dailyDataFiltered" | translate }}
        </div>
      }
      <br />
      @if (correlationForm.get('aggregation').value) {
        <div
          class="data-filtered-message"
          >
          <mat-icon class="mr-1">info</mat-icon> {{ "app.dataThinned" | translate }}
        </div>
      }
    </mat-card-subtitle>
    <div class="ml-auto">
      <button mat-button [matMenuTriggerFor]="correlationContextMenu">
        <mat-icon>more_vert</mat-icon>
      </button>
      <mat-menu #correlationContextMenu="matMenu">
        <button mat-menu-item (click)="exportCurrentView($event)">
          {{ "app.exportView" | translate }} <mat-icon>file_download</mat-icon>
        </button>
        <button mat-menu-item (click)="exportAsCSV($event)">
          {{ "app.exportDataset" | translate }} <mat-icon>file_download</mat-icon>
        </button>
      </mat-menu>
    </div>
  </mat-card-header>

  <!-- Custom Legend Component -->
  <div class="flex justify-center">
    <app-correlation-legend
      [chart]="getChartInstance()"
      [preserveVisibility]="true"
      >
    </app-correlation-legend>
  </div>

  <mat-card-content>
    <div class="relative">
      @if (isLoading) {
        <div data-test-id="module-loading-spinner">
          <ngx-spinner
            [fullScreen]="false"
            type="ball-scale-multiple"
            size="medium"
            class="text-white"
            >
            {{ 'app.loadingData' | translate }}
          </ngx-spinner>
        </div>
      }
      <div [ngStyle]="{ width: chartWidth }">
        <canvas
          #chart
          id="chart"
          baseChart
          [datasets]="chartData"
          [type]="'line'"
          [plugins]="chartPlugins"
          [options]="chartOptions"
          >
        </canvas>

        <div class="filter-group">
          <form [formGroup]="correlationForm">
            <mat-form-field appearance="fill">
              <mat-label>{{ "app.parameter" | translate }}</mat-label>
              <mat-select
                (selectionChange)="selectionParameterChanged($event)"
                [formControl]="selectedFieldControl"
                name="selectedField"
                [compareWith]="compareFields"
                >
                @for (field of availableFields | keyvalue; track field) {
                  <mat-option
                    matInput
                    [value]="field.value"
                    >{{ field.value.field_description | translateField }}</mat-option
                    >
                  }
                </mat-select>
              </mat-form-field>

              <!-- Date Range Options -->
              <mat-form-field appearance="fill">
                <mat-label>{{ "app.displayTimePeriod" | translate }}</mat-label>
                <mat-select
                  (selectionChange)="dateRangeSelectionChanged($event)"
                  [formControl]="preDefinedDatePeriod"
                  name="preDefinedDateField"
                  data-test-id="module-date-range-select"
                  >
                  @for (timePeriod of selectableTimePeriods; track timePeriod) {
                    <mat-option
                      matInput
                      [value]="timePeriod.value"
                      >{{ timePeriod.viewValue }}</mat-option
                      >
                    }
                  </mat-select>
                </mat-form-field>

                <!-- Date Range Filter -->
                @if (customDateRange) {
                  <mat-form-field
                    [color]="'accent'"
                    appearance="fill"
                    >
                    <mat-label>{{ "app.filterTimePeriod" | translate }}</mat-label>
                    <mat-date-range-input
                      [formGroup]="correlationForm"
                      [rangePicker]="datepicker"
                      [min]="minDate"
                      [max]="maxDate"
                      >
                      <input
                        matStartDate
                        type="text"
                        matInput
                        formControlName="filterDateStart"
                        placeholder="dd.mm.yyyy"
                        data-test-id="module-custom-date-start"
                        />
                      <input
                        matEndDate
                        type="text"
                        matInput
                        formControlName="filterDateEnd"
                        placeholder="dd.mm.yyyy"
                        data-test-id="module-custom-date-end"
                        />
                    </mat-date-range-input>
                    <mat-datepicker-toggle
                      matSuffix
                      [for]="datepicker"
                    ></mat-datepicker-toggle>
                    <mat-date-range-picker #datepicker (closed)="customDateFilterChanged($event)">
                      <mat-datepicker-actions>
                        <button mat-button matDatepickerCancel>Schließen</button>
                        <button
                          mat-raised-button
                          color="primary"
                          matDatepickerApply
                          >
                          Auswählen
                        </button>
                      </mat-datepicker-actions>
                    </mat-date-range-picker>
                  </mat-form-field>
                }

                <!-- Low Pass Filter Options -->
                <mat-form-field appearance="fill">
                  <mat-label>{{ "app.smoothingTimePeriod" | translate }}</mat-label>
                  <mat-select
                    (selectionChange)="selectionChanged($event)"
                    [formControl]="preDefinedLowPassFilterPeriods"
                    name="preDefinedLowPassFilter"
                    data-test-id="module-smoothing-select"
                    >
                    @for (
                      lowPassFilterOption of selectableLowPassFilterPeriods
                      ; track
                      lowPassFilterOption) {
                      <mat-option
                        matInput
                        [value]="lowPassFilterOption.value"
                        >{{ lowPassFilterOption.viewValue }}</mat-option
                        >
                      }
                    </mat-select>
                  </mat-form-field>
                  <div class="flex">
                    <!-- Texts Container -->
                    <div class="flex flex-col mr-2 justify-center align-middle">
                      @if (config.relative_zero) {
                        <span>{{ "app.absoluteDeformation" | translate }}</span>
                      }
                      <span>{{ "app.displayAllData" | translate }}</span>
                    </div>
                    <!-- Slide Toggles Container -->
                    <div class="flex flex-col">
                      @if (config.relative_zero) {
                        <mat-slide-toggle
                          (change)="relativeZeroChanged($event)"
                          >
                          {{ "app.relativeDeformation" | translate }}
                        </mat-slide-toggle>
                      }
                      <mat-slide-toggle
                        formControlName="aggregation"
                        (change)="aggregationChanged($event)"
                        >
                        {{ "app.thinData" | translate }}
                      </mat-slide-toggle>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </mat-card-content>
        @if (userService.userIsAdmin) {
          <mat-card-actions>
            <button
              mat-button
              (click)="reportModuleService.openEditReportModuleForm(config.guid)"
              >
              BEARBEITEN
            </button>
            <button
              (click)="reportModuleService.removeModuleFormReport(config.guid)"
              mat-button
              >
              LÖSCHEN
            </button>
          </mat-card-actions>
        }
      </mat-card>
